"use client";

import React, { useCallback, useState } from "react";
import { Tv } from "lucide-react";
import { Modal } from "@/components/ui/modals/Modal";
import type { Labels } from "@/lib/labels/types";
import type { ResolvedListings } from "../utils/resolveMatchListings";
import { AllBroadcastersDialog } from "./AllBroadcastersDialog";

interface Props {
    matchNativeId: string;
    labels: Labels;
    /** Whether this match has at least one broadcaster assigned. Parent must know this (via batch pre-fetch). */
    hasBroadcasters: boolean;
    /** Optional pre-resolved listings. If omitted, the modal lazy-fetches on first open. */
    resolved?: ResolvedListings;
    size?: "sm" | "md";
    className?: string;
}

/**
 * Small TV icon rendered on match list rows / cards / blocks.
 * - Renders nothing when `hasBroadcasters` is false.
 * - Click → opens the AllBroadcastersModal. If `resolved` is not provided, fetches on demand.
 */
export function MatchTvIndicator({
    matchNativeId,
    labels,
    hasBroadcasters,
    resolved: resolvedProp,
    size = "sm",
    className,
}: Props) {
    const [open, setOpen] = useState(false);
    const [resolved, setResolved] = useState<ResolvedListings | null>(resolvedProp ?? null);
    const [loading, setLoading] = useState(false);

    const handleOpen = useCallback(async () => {
        setOpen(true);
        if (resolved) return;
        setLoading(true);
        try {
            const res = await fetch(
                `/api/match-tv-listings/resolve?match=${encodeURIComponent(matchNativeId)}`,
            );
            if (!res.ok) throw new Error("fetch failed");
            const data = (await res.json()) as ResolvedListings;
            setResolved(data);
        } catch {
            /* leave resolved null; dialog shows empty */
        } finally {
            setLoading(false);
        }
    }, [matchNativeId, resolved]);

    if (!hasBroadcasters) return null;

    const iconClass = size === "sm" ? "w-3.5 h-3.5" : "w-4 h-4";
    const buttonClass = [
        "inline-flex items-center justify-center shrink-0 cursor-pointer",
        size === "sm" ? "w-5 h-5" : "w-6 h-6",
        "rounded-full opacity-70 hover:opacity-100 transition-opacity",
        className,
    ]
        .filter(Boolean)
        .join(" ");

    return (
        <>
            <button
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleOpen();
                }}
                className={buttonClass}
                aria-label={labels.tv.watchOn}
                title={labels.tv.watchOn}
            >
                <Tv className={iconClass} aria-hidden="true" />
            </button>
            <Modal isOpen={open} onClose={() => setOpen(false)}>
                {loading ? (
                    <div className="p-5 min-w-80 text-sm opacity-70">{labels.common.loading}</div>
                ) : resolved ? (
                    <AllBroadcastersDialog resolved={resolved} labels={labels} />
                ) : (
                    <div className="p-5 min-w-80 text-sm opacity-70">{labels.common.noResults}</div>
                )}
            </Modal>
        </>
    );
}
